import * as React from 'react';

import { typographyVariants } from '../typography/typography';
import { cn } from '../../utils';

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className="relative w-full">
      <table className={cn('w-full caption-bottom text-sm', className)} ref={ref} {...props} />
    </div>
  ),
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    className={cn(
      '[&_tr]:border-b border-t border-border-weak text-weak bg-table-background',
      typographyVariants({ variant: 'labelSmallBook' }),
      className,
    )}
    translate="no"
    ref={ref}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    className={cn('[&_tr:last-child]:border-0 bg-background', className)}
    ref={ref}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    className={cn('border-t font-medium [&>tr]:last:border-b-0 bg-background', className)}
    ref={ref}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      className={cn(
        'border-b border-border-weak transition-colors hover:bg-table-background/90 data-[state=selected]:bg-table-background/80',
        typographyVariants({ variant: 'labelMediumBook' }),
        className,
      )}
      ref={ref}
      {...props}
    />
  ),
);
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement> & {
    className?: string;
  }
>(({ className, ...props }, ref) => (
  <th
    className={cn(
      'h-10 px-4 text-left align-middle [&:has([role=checkbox])]:pr-0 whitespace-nowrap',
      typographyVariants({ variant: 'labelSmallBook' }),
      className,
    )}
    ref={ref}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & {
    className?: string;
  }
>(({ className, ...props }, ref) => (
  <td
    className={cn('p-3.5 align-middle [&:has([role=checkbox])]:pr-0', className)}
    // Google Translate plugin breaks the application if translate="no" is not set
    // https://github.com/facebook/react/issues/11538
    translate="no"
    ref={ref}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption className={cn('mt-4 text-sm', className)} ref={ref} {...props} />
));
TableCaption.displayName = 'TableCaption';

export { TableCaption, TableHeader, TableFooter, TableBody, TableHead, TableCell, TableRow, Table };
